// PathsListPanel.jsx — Hierarchical sidebar showing all independent paths and
// their lanes. Replaces PathCreatorPanelContent for the new multi-path model.

import React, { useState, useRef, useEffect } from "react";
import { LANE_TYPE_LABELS } from "./rsaPathTypes";
import { getPathLengthMiles } from "./PathsPanelWaypoints";
import { lengthMismatch } from "../../../../utils/stateLrs/lrsMath";
import { SEGMENT_COLORS } from "../InternalComponents/rsaPathUtils";
import { backdropCloseHandlers } from "../../../../utils/backdropClose";
import "./PathsListPanel.css";
import { selectOrthoPhase, selectOrthoProgress } from "./orthoJobView";
import { isRouteGeometryDirty } from "./routeGeometryHash";
import LrsRoutePanel from "./LrsRoutePanel";

// ─── Speed helpers ───────────────────────────────────────────────────────────

/** Compute total lane length in miles from waypoints or GeoJSON coords */
function getLaneLengthMiles(lane) {
  const coords = lane?.pathData?.geometry?.coordinates;
  if (coords?.length >= 2) return getPathLengthMiles(coords);
  if (lane?.waypoints?.length >= 2) return getPathLengthMiles(lane.waypoints);
  return 0;
}

/** Format mile mark: 0.00, 0.45, etc. */
function fmtMile(mi) {
  return mi.toFixed(2);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtLength(pathData, waypoints) {
  const miles =
    pathData?.geometry?.coordinates?.length >= 2
      ? getPathLengthMiles(pathData.geometry.coordinates)
      : getPathLengthMiles(waypoints);
  if (miles == null) return null;
  return miles < 0.1
    ? `${(miles * 5280).toFixed(0)} ft`
    : `${miles.toFixed(2)} mi`;
}

/** LRS length sanity check (spec: warn when |actual − expected| > 0.1 + 5%).
 *  Returns a warning string or null. Only applies to LRS-created paths whose
 *  Google-resolved geometry has arrived. */
function getLrsLengthWarning(path) {
  const lrs = path?.lrs;
  const coords = path?.pathData?.geometry?.coordinates;
  if (!lrs || !Number.isFinite(lrs.mpStart) || !Number.isFinite(lrs.mpEnd)) return null;
  if (!coords || coords.length < 2) return null;
  const expected = Math.abs(lrs.mpEnd - lrs.mpStart);
  const actual = getPathLengthMiles(coords);
  if (actual == null) return null;
  const { mismatch } = lengthMismatch(expected, actual);
  if (!mismatch) return null;
  return `Resolved route is ${actual.toFixed(2)} mi but the ${
    lrs.network === 'milepoint' ? 'milepoint' : 'milepost'
  } span is ${expected.toFixed(2)} mi — the router may have left the intended road. Adjust the endpoints or recreate with map clicks.`;
}

function laneCompleteness(lane) {
  const hasGeometry =
    (lane?.waypoints?.length ?? 0) >= 2 ||
    (lane?.pathData?.geometry?.coordinates?.length ?? 0) >= 2;
  const hasSpeeds =
    Array.isArray(lane?.speedSegments) && lane.speedSegments.length > 0;
  return { hasGeometry, hasSpeeds, ready: hasGeometry && hasSpeeds };
}

function InlineLabel({
  value,
  onChange,
  disabled = false,
  leadingIcon = null,
  forceEdit = false,
  normalizeValue = (nextValue) => nextValue,
  placeholder = undefined,
}) {
  const [draft, setDraft] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const shouldRevertOnBlurRef = useRef(false);

  useEffect(() => {
    if (!isFocused) {
      setDraft(value);
    }
  }, [value, isFocused]);

  useEffect(() => {
    if (forceEdit && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [forceEdit]);

  const commitDraft = () => {
    const nextValue = normalizeValue(draft).trim() || value;
    setDraft(nextValue);
    if (nextValue !== value) {
      onChange(nextValue);
    }
  };

  return (
    <span
      className="paths-list-label-text"
      title={disabled ? undefined : "Edit name"}
    >
      {leadingIcon}
      <input
        ref={inputRef}
        className="paths-list-label-value paths-list-label-input paths-list-label-input--inline"
        value={draft}
        onChange={(e) => setDraft(normalizeValue(e.target.value))}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          if (shouldRevertOnBlurRef.current) {
            shouldRevertOnBlurRef.current = false;
            setDraft(value);
            return;
          }
          commitDraft();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          e.nativeEvent?.stopImmediatePropagation?.();
          if (e.key === "Enter") {
            e.preventDefault();
            inputRef.current?.blur();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            shouldRevertOnBlurRef.current = true;
            inputRef.current?.blur();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={60}
      />
    </span>
  );
}

const normalizeLaneLabel = (value) =>
  String(value ?? "").replace(/[\s_-]/g, "");

function LaneColorDropdown({
  colorIndex = 0,
  segmentColors = SEGMENT_COLORS,
  onChange,
  label = "Lane color",
}) {
  const [open, setOpen] = useState(false);
  const colors = segmentColors.slice(0, 10);
  const safeIndex = colors.length
    ? (((Number(colorIndex) || 0) % colors.length) + colors.length) %
      colors.length
    : 0;
  const color = colors[safeIndex] || "#93c5fd";

  return (
    <span
      className="paths-list-color-picker"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className={`paths-list-color-picker-trigger${open ? " paths-list-color-picker-trigger--open" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: color ${safeIndex + 1}`}
        title={`${label}: color ${safeIndex + 1}`}
      >
        <i
          className="fa-solid fa-location-dot paths-list-dot"
          style={{ color }}
          aria-hidden
        />
        <i
          className="fa-solid fa-chevron-down paths-list-color-picker-caret"
          aria-hidden
        />
      </button>
      {open && (
        <span className="paths-list-color-picker-menu" role="listbox">
          {colors.map((optionColor, index) => (
            <button
              key={`${optionColor}-${index}`}
              type="button"
              className={`paths-list-color-picker-option${index === safeIndex ? " paths-list-color-picker-option--selected" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(index);
                setOpen(false);
              }}
              role="option"
              aria-selected={index === safeIndex}
              aria-label={`Use lane color ${index + 1}`}
              title={`Color ${index + 1}`}
            >
              <i
                className="fa-solid fa-location-dot"
                style={{ color: optionColor }}
                aria-hidden
              />
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

// ─── Inline Offset Form ───────────────────────────────────────────────────────

function InlineOffsetForm({
  paths,
  lanes,
  activePathId,
  wktAvailable,
  onCreateOffsetLane,
  onCancel,
  mode = "offset",
}) {
  const pathOptions = (paths || []).filter(
    (p) => p.pathData?.geometry?.coordinates?.length >= 2,
  );
  const laneOptions = (lanes || []).filter(
    (l) =>
      l.pathData?.geometry?.coordinates?.length >= 2 ||
      (l.waypoints?.length ?? 0) >= 2,
  );
  const [sourcePathId, setSourcePathId] = useState(
    activePathId ?? pathOptions[0]?.id ?? "",
  );
  const [directionMode, setDirectionMode] = useState("same");
  const [side, setSide] = useState("right");
  const isCopyMode = mode === "copy";
  const [offsetFeet, setOffsetFeet] = useState(isCopyMode ? "0" : "6");
  const [reverseCopy, setReverseCopy] = useState(false);
  const [creating, setCreating] = useState(false);

  // Sync sourcePathId when activePathId changes
  useEffect(() => {
    if (activePathId && activePathId !== sourcePathId)
      setSourcePathId(activePathId);
  }, [activePathId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOffsetFeet(isCopyMode ? "0" : "6");
  }, [isCopyMode]);

  const parsedOffsetFeet = Number(offsetFeet);
  const hasValidOffset =
    offsetFeet !== "" &&
    Number.isFinite(parsedOffsetFeet) &&
    parsedOffsetFeet >= 0;
  const canCreate =
    (pathOptions.length > 0 || (isCopyMode && laneOptions.length > 0)) &&
    wktAvailable &&
    hasValidOffset;

  async function handleCreate() {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      await onCreateOffsetLane?.({
        sourcePathId:
          sourcePathId ||
          pathOptions[0]?.id ||
          (isCopyMode ? laneOptions[0]?.id : undefined),
        directionMode: isCopyMode
          ? reverseCopy
            ? "reverse"
            : "same"
          : directionMode,
        side: isCopyMode ? "right" : side,
        offsetFeet: isCopyMode ? 0 : parsedOffsetFeet,
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      className="paths-list-inline-form paths-list-inline-form--offset"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="paths-list-step-label paths-list-step-label--outer">
        <button
          type="button"
          className="paths-list-back-btn"
          onClick={onCancel}
          title="Go back"
          aria-label="Go back to lane picker"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden />
        </button>
        <span className="paths-list-step-badge paths-list-step-badge--current">
          2
        </span>
        Step 2 of 3 · Add Lane
      </p>
      <div className="paths-list-inline-form-header">
        <div className="paths-list-inline-form-header-copy">
          <p className="paths-list-inline-form-title">
            {isCopyMode ? "Copy Lane" : "Create Offset Lane"}
          </p>
          <p className="paths-list-inline-form-subtitle">
            {isCopyMode
              ? "Duplicate a routed path as a new lane. Optionally reverse to swap start and end."
              : "Generate a lane from a routed path using side, offset distance, and direction settings."}
          </p>
        </div>
        <span className="paths-list-inline-form-badge">
          {isCopyMode ? "Copy" : "Offset"}
        </span>
      </div>
      {!wktAvailable && (
        <p className="paths-list-inline-form-warn">
          WKT coordinate system required — set up in Setup tab.
        </p>
      )}
      <div className="paths-list-inline-form-fields">
        <label className="paths-list-inline-form-field">
          <span>Source path</span>
          <select
            value={sourcePathId}
            onChange={(e) => setSourcePathId(e.target.value)}
            disabled={
              (pathOptions.length === 0 &&
                (!isCopyMode || laneOptions.length === 0)) ||
              creating
            }
          >
            {pathOptions.length === 0 &&
            (!isCopyMode || laneOptions.length === 0) ? (
              <option value="">No routed path available</option>
            ) : (
              <>
                {pathOptions.length > 0 && (
                  <optgroup label="Routes">
                    {pathOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </optgroup>
                )}
                {isCopyMode && laneOptions.length > 0 && (
                  <optgroup label="Lanes">
                    {laneOptions.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.label}
                      </option>
                    ))}
                  </optgroup>
                )}
              </>
            )}
          </select>
        </label>
        {!isCopyMode && (
          <div className="paths-list-inline-form-row">
            <label className="paths-list-inline-form-field">
              <span>Side</span>
              <select
                value={side}
                onChange={(e) => setSide(e.target.value)}
                disabled={creating}
              >
                <option value="right">Right</option>
                <option value="left">Left</option>
              </select>
            </label>
            <label className="paths-list-inline-form-field">
              <span>Offset (ft)</span>
              <input
                className="paths-list-inline-form-offset-input"
                type="number"
                min="0"
                step="0.1"
                value={offsetFeet}
                onChange={(e) => setOffsetFeet(e.target.value)}
                disabled={creating}
              />
            </label>
            <label className="paths-list-inline-form-field">
              <span>Direction</span>
              <select
                value={directionMode}
                onChange={(e) => setDirectionMode(e.target.value)}
                disabled={creating}
              >
                <option value="same">Same</option>
                <option value="reverse">Reverse</option>
              </select>
            </label>
          </div>
        )}
        {isCopyMode && (
          <label className="paths-list-inline-form-checkbox">
            <input
              type="checkbox"
              checked={reverseCopy}
              onChange={(e) => setReverseCopy(e.target.checked)}
              disabled={creating}
            />
            <span className="paths-list-inline-form-checkbox-label">
              Reverse direction (swap start/end)
            </span>
          </label>
        )}
      </div>
      <div className="paths-list-inline-form-actions">
        <button
          type="button"
          className="paths-list-inline-form-create"
          onClick={handleCreate}
          disabled={!canCreate || creating}
        >
          <i
            className={`fas ${creating ? "fa-spinner fa-spin" : "fa-solid fa-plus"}`}
            aria-hidden
          />
          {creating ? "Creating…" : "Create Lane"}
        </button>
      </div>
    </div>
  );
}

// ─── Manual Lane Controls ─────────────────────────────────────────────────────

function InlineManualControls({
  pathId,
  manualLaneCreationActive,
  manualLanePointCount,
  onStartManualLane,
  onStopManualLane,
  onCancel,
  onBackToPicker,
}) {
  return (
    <div
      className="paths-list-inline-form paths-list-inline-form--manual"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="paths-list-step-label paths-list-step-label--outer">
        <button
          type="button"
          className="paths-list-back-btn"
          onClick={() => {
            onStopManualLane?.();
            onBackToPicker?.();
          }}
          title="Go back"
          aria-label="Go back to lane picker"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden />
        </button>
        <span className="paths-list-step-badge paths-list-step-badge--current">
          2
        </span>
        Step 2 of 3 · Add Lane
      </p>
      <div className="paths-list-inline-form-header">
        <div className="paths-list-inline-form-header-copy">
          <p className="paths-list-inline-form-title">Manual Lane Drawing</p>
          <p className="paths-list-inline-form-subtitle">
            Place lane points directly on the map and finish when the geometry
            looks right.
          </p>
        </div>
        {manualLaneCreationActive && (
          <button
            type="button"
            className="paths-list-inline-form-done"
            onClick={() => {
              onStopManualLane?.();
              onCancel();
            }}
            title="Done"
            aria-label="Done"
          >
            <i className="fas fa-check" aria-hidden />
          </button>
        )}
        <span className="paths-list-inline-form-badge">
          {manualLaneCreationActive
            ? `${manualLanePointCount} pt${manualLanePointCount !== 1 ? "s" : ""}`
            : "Manual"}
        </span>
      </div>
      <p className="paths-list-inline-form-hint">
        {manualLaneCreationActive
          ? `Placing points on the map… ${manualLanePointCount} point${manualLanePointCount !== 1 ? "s" : ""} placed`
          : "Click the map to place lane points, then finish when done."}
      </p>
      {!manualLaneCreationActive && (
        <div className="paths-list-inline-form-actions">
          <button
            type="button"
            className="paths-list-inline-form-create"
            onClick={() => onStartManualLane?.(pathId)}
          >
            <i className="fas fa-pen" aria-hidden /> Start placing points
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Lane Row ─────────────────────────────────────────────────────────────────

const LANE_POSITION_OPTIONS = ["LL", "CL", "RL"];
const LANE_DIRECTION_OPTIONS = ["N", "P"];

function LaneRow({
  lane,
  isActive,
  laneEditMode,
  editingLaneId,
  speedEditMode,
  segmentColors,
  manualLaneCreationActive,
  selectedLaneWaypointIndices,
  onClearLaneSelection,
  onSelect,
  onDelete,
  onToggleVisible,
  onRename,
  onChangeLaneColor,
  onChangeLanePosition,
  onChangeLaneDirection,
  onEditPoints,
  onExitEditPoints,
  onEnterSpeedMode,
  onExitSpeedMode,
  onUpdateSpeedAtBreakpoint,
  onUpdateSpeedBreakpointMile,
  onAddSpeedBreakpoint,
  onRemoveSpeedBreakpoint,
  onResumeManualLane,
  onStopManualLane,
  onMarkReviewed,
  onEnterBridgeManually,
  laneTraceProgress = {},
}) {
  const lenLabel = fmtLength(lane.pathData, lane.waypoints);
  const totalMiles = getLaneLengthMiles(lane);
  const laneTypeLabel = LANE_TYPE_LABELS[lane.laneType] || "Lane";
  const isThisEditing = laneEditMode && editingLaneId === lane.id;
  const isThisSpeedEditing = speedEditMode && editingLaneId === lane.id;
  const { hasGeometry, hasSpeeds } = laneCompleteness(lane);
  // Legacy lanes saved before the `reviewed` field existed → treat `undefined`
  // as reviewed so they keep showing as --ready after rehydration.
  const editReviewed = lane.reviewed !== false;
  const inContinueMode = manualLaneCreationActive && isActive;
  // True while the user is laying down the first set of manual points on
  // this lane. Used to suppress the LaneRow checklist (Step 2 "Speeds"
  // placeholder) so it doesn't render alongside InlineManualControls's
  // "Step 2 · Add Lane" — which made it look like a phantom second lane.
  const isInitialManualPlacement =
    manualLaneCreationActive && isActive && !hasSpeeds;
  const [showZeroSpeedError, setShowZeroSpeedError] = useState(false);
  const [editingMileRowIdx, setEditingMileRowIdx] = useState(null);
  // Tracks when the current press started inside an editable field so we can
  // ignore the synthetic click fired by browsers when the user drags outside
  // to release a text selection.
  const dragFromEditableRef = useRef(false);
  // First speed input in the table — focused automatically when the user
  // lands on the speed selector step so they can start typing immediately.
  const firstSpeedInputRef = useRef(null);
  const ready = hasGeometry && hasSpeeds && editReviewed;
  const canResumeManual = lane.creationMode === "manual";

  // Focus (and select) the first speed field the moment speed mode opens.
  useEffect(() => {
    if (isThisSpeedEditing && firstSpeedInputRef.current) {
      firstSpeedInputRef.current.focus();
      firstSpeedInputRef.current.select();
    }
  }, [isThisSpeedEditing]);

  // Auto-enter speed mode when lane has geometry but no speeds yet —
  // the "Set speeds" button is omitted; speed mode is the step itself.
  // Suppressed while the user is still placing manual-lane points so the
  // 2nd click doesn't yank them out of placement; once they hit Done and
  // manualLaneCreationActive flips false, this re-runs and advances.
  useEffect(() => {
    if (
      hasGeometry &&
      !hasSpeeds &&
      !speedEditMode &&
      isActive &&
      !manualLaneCreationActive
    ) {
      onEnterSpeedMode?.(lane.id);
    }
  }, [
    hasGeometry,
    hasSpeeds,
    speedEditMode,
    isActive,
    manualLaneCreationActive,
    lane.id,
    onEnterSpeedMode,
  ]);
  const speedSegments = Array.isArray(lane.speedSegments)
    ? [...lane.speedSegments]
        .filter((seg) => Number.isFinite(Number(seg?.mileMark)))
        .sort((a, b) => Number(a.mileMark) - Number(b.mileMark))
    : [];

  const hasLaneGeometry =
    lane.waypoints?.length > 1 ||
    lane.pathData?.geometry?.coordinates?.length > 1;

  const showLaneChecklist =
    hasGeometry &&
    !isThisSpeedEditing &&
    !isInitialManualPlacement &&
    (!ready || isThisEditing || inContinueMode);
  const showSpeedSummary = speedSegments.length > 0 || isThisSpeedEditing;
  const attachSpeedSummaryToChecklist =
    showLaneChecklist && speedSegments.length > 0 && !isThisSpeedEditing;

  const offsetInfo =
    lane.creationMode === "offset" && lane.offsetFeet != null
      ? ` · ${lane.offsetFeet} ft ${lane.side ?? ""}`
      : "";

  return (
    <div
      className={`paths-list-lane-group${isActive || ready ? " paths-list-lane-group--active" : ""}${ready ? " paths-list-lane-group--ready" : ""}${isActive ? " paths-list-lane-group--selected" : ""}`}
      onMouseDown={(e) => {
        // Remember if the press started inside an editable field. If so, we
        // skip the synthetic click that fires on drag-out text selection
        // (e.g. selecting the "0" in a speed input and releasing outside).
        dragFromEditableRef.current = Boolean(
          e.target?.closest?.("input, textarea, select"),
        );
      }}
      onClick={(e) => {
        if (dragFromEditableRef.current) {
          dragFromEditableRef.current = false;
          return;
        }
        onSelect(lane.id);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(lane.id);
        }
      }}
      aria-pressed={isActive}
      aria-label={`Select lane ${lane.label}`}
    >
      <div
        className={`paths-list-lane-row${isActive || ready ? " paths-list-lane-row--active" : ""}${isThisEditing ? " paths-list-lane-row--editing" : ""}${isThisSpeedEditing ? " paths-list-lane-row--speed" : ""}${ready ? " paths-list-lane-row--ready" : ""}${!ready ? " paths-list-lane-row--preview" : ""}`}
      >
        <div className="paths-list-lane-top">
          {!ready ? (
            <div className="paths-list-lane-info">
              <div className="paths-list-lane-edit-row">
                <span className="paths-list-label-text">
                  <LaneColorDropdown
                    colorIndex={lane.colorIndex ?? 0}
                    segmentColors={segmentColors}
                    onChange={(nextColorIndex) =>
                      onChangeLaneColor?.(lane.id, nextColorIndex)
                    }
                  />
                  <span className="paths-list-label-value">New Lane</span>
                </span>
              </div>
              <span className="paths-list-meta">—</span>
            </div>
          ) : (
            <div className="paths-list-lane-info">
              <div className="paths-list-lane-edit-row">
                <InlineLabel
                  value={lane.label}
                  onChange={(v) => onRename(lane.id, v)}
                  normalizeValue={normalizeLaneLabel}
                  forceEdit={isThisEditing}
                  leadingIcon={
                    <LaneColorDropdown
                      colorIndex={lane.colorIndex ?? 0}
                      segmentColors={segmentColors}
                      onChange={(nextColorIndex) =>
                        onChangeLaneColor?.(lane.id, nextColorIndex)
                      }
                    />
                  }
                />
                {isThisEditing && (
                  <>
                    <select
                      className="paths-list-label-input paths-list-lane-position-select"
                      value={lane.lanePosition ?? "LL"}
                      onChange={(e) =>
                        onChangeLanePosition?.(lane.id, e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                    >
                      {LANE_POSITION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <select
                      className="paths-list-label-input paths-list-lane-position-select paths-list-lane-direction-select"
                      value={lane.direction ?? "P"}
                      onChange={(e) =>
                        onChangeLaneDirection?.(lane.id, e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                    >
                      {LANE_DIRECTION_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <span className="paths-list-meta">
                {laneTypeLabel}
                {offsetInfo}
                {lenLabel ? ` · ${lenLabel}` : ""}
                {lane.waypoints?.length > 0
                  ? ` · ${lane.waypoints.length} pts`
                  : ""}
              </span>
            </div>
          )}
          <div className="paths-list-lane-controls">
            <div
              className="paths-list-lane-actions"
              onClick={(e) => e.stopPropagation()}
            >
              {!isThisEditing && !isThisSpeedEditing && hasLaneGeometry && (
                <button
                  type="button"
                  className="paths-list-action-btn paths-list-action-btn--edit"
                  onClick={() => onEditPoints(lane.id)}
                  title="Edit lane points — hold Ctrl to lasso-select"
                  aria-label="Edit lane points"
                >
                  <i className="fa-solid fa-pen-to-square" aria-hidden />
                </button>
              )}
              {isThisEditing && (
                <button
                  type="button"
                  className="paths-list-action-btn paths-list-action-btn--done"
                  onClick={() => onExitEditPoints()}
                  title="Finish editing lane points"
                  aria-label="Finish editing"
                >
                  <i className="fa-solid fa-check" aria-hidden />
                </button>
              )}
              {!isThisEditing && !isThisSpeedEditing && hasLaneGeometry && (
                <button
                  type="button"
                  className="paths-list-action-btn paths-list-action-btn--speed"
                  onClick={() => onEnterSpeedMode(lane.id)}
                  title="Configure speed limits for this lane"
                  aria-label="Configure speed"
                >
                  <svg
                    width="1em"
                    height="1em"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M3 17 a 9 9 0 0 1 18 0" />
                    <line x1="12" y1="17" x2="17" y2="9.5" />
                    <circle
                      cx="12"
                      cy="17"
                      r="1.6"
                      fill="currentColor"
                      stroke="none"
                    />
                  </svg>
                </button>
              )}
              {isThisSpeedEditing && (
                <button
                  type="button"
                  className="paths-list-action-btn paths-list-action-btn--done"
                  onClick={() => onExitSpeedMode()}
                  title="Finish speed configuration"
                  aria-label="Finish speed editing"
                >
                  <i className="fa-solid fa-check" aria-hidden />
                </button>
              )}
              {lane.autoTraced &&
                lane.creationMode === "manual" &&
                !laneTraceProgress[lane.pathId] && (
                  <button
                    type="button"
                    className="paths-list-lane-btn paths-list-lane-btn--bridge"
                    onClick={() =>
                      onEnterBridgeManually?.(lane.id, lane.pathId)
                    }
                    title="Edit waypoints and resume auto-trace from the tail"
                  >
                    <i className="fas fa-pen-to-square" aria-hidden /> Edit /
                    Resume
                  </button>
                )}
              <button
                type="button"
                className="paths-panel-waypoints-eye-btn"
                onClick={() => onToggleVisible(lane.id)}
                title={lane.visible ? "Hide lane" : "Show lane"}
                aria-label={lane.visible ? "Hide lane" : "Show lane"}
              >
                <i
                  className={`fas ${lane.visible ? "fa-eye" : "fa-eye-slash"}`}
                  aria-hidden
                />
              </button>
              <button
                type="button"
                className="paths-panel-waypoints-delete-segment-btn"
                onClick={() => onDelete(lane.id)}
                title="Delete lane"
                aria-label="Delete lane"
              >
                <i className="fa-solid fa-trash-can" aria-hidden />
              </button>
            </div>
            {ready && !isThisEditing && (
              <div className="paths-list-lane-position-badges">
                <span className="paths-list-lane-position-badge">
                  {lane.lanePosition ?? "LL"}
                </span>
                <span className="paths-list-lane-position-badge">
                  {lane.direction ?? "P"}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {showLaneChecklist && (
        <div
          className={`paths-list-lane-checklist${attachSpeedSummaryToChecklist ? " paths-list-lane-checklist--with-speed-summary" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!hasSpeeds && (
            <div className="paths-list-inline-form paths-list-inline-form--step2">
              <p className="paths-list-step-label paths-list-step-label--outer">
                <span className="paths-list-step-badge paths-list-step-badge--pending">
                  2
                </span>
                Step 2 of 3 · Speeds
              </p>
              <div className="paths-list-inline-form-header">
                <div className="paths-list-inline-form-header-copy">
                  <p className="paths-list-inline-form-title">Lane Speeds</p>
                  <p className="paths-list-inline-form-subtitle">
                    Entering speed mode…
                  </p>
                </div>
                <span className="paths-list-inline-form-badge">
                  {speedSegments.length > 0
                    ? `${speedSegments.length} SEG${speedSegments.length !== 1 ? "S" : ""}`
                    : "0 SEGS"}
                </span>
              </div>
            </div>
          )}
          {hasSpeeds && (!editReviewed || isThisEditing || inContinueMode) && (
            <div className="paths-list-inline-form paths-list-inline-form--step3">
              <p className="paths-list-step-label paths-list-step-label--outer">
                <button
                  type="button"
                  className="paths-list-back-btn"
                  onClick={() => {
                    if (inContinueMode) {
                      onStopManualLane?.();
                      return;
                    }
                    if (isThisEditing) {
                      onExitEditPoints?.();
                      return;
                    }
                    // Rewinding to step 2 — clear the reviewed flag so step 3
                    // re-appears once speeds are re-entered.
                    onMarkReviewed?.(lane.id, false);
                    const total = lane.speedSegments?.length ?? 0;
                    for (let i = total - 1; i >= 0; i--) {
                      onRemoveSpeedBreakpoint?.(lane.id, i);
                    }
                    onEnterSpeedMode?.(lane.id);
                  }}
                  title={
                    inContinueMode
                      ? "Go back — exit continue lane"
                      : isThisEditing
                        ? "Go back — exit edit mode"
                        : "Go back — reconfigure speeds"
                  }
                  aria-label={
                    inContinueMode
                      ? "Go back — exit continue lane"
                      : isThisEditing
                        ? "Go back — exit edit mode"
                        : "Go back — reconfigure speeds"
                  }
                >
                  <i className="fa-solid fa-arrow-left" aria-hidden />
                </button>
                <span className="paths-list-step-badge paths-list-step-badge--current">
                  3
                </span>
                Step 3 of 3 · Edit (optional)
              </p>
              <div className="paths-list-speed-summary-meta-start">
                <span className="paths-list-speed-summary-badge">
                  {inContinueMode
                    ? "Continue Lane"
                    : isThisEditing
                      ? "Edit Mode"
                      : "Lane Review"}
                </span>
                <button
                  type="button"
                  className="paths-list-inline-form-done"
                  onClick={() => {
                    if (inContinueMode) {
                      onStopManualLane?.();
                    } else if (isThisEditing) {
                      onExitEditPoints?.();
                    } else {
                      onMarkReviewed?.(lane.id, true);
                    }
                  }}
                  title={
                    inContinueMode
                      ? "Done — exit continue lane"
                      : isThisEditing
                        ? "Done — exit edit mode"
                        : "Done — mark lane as reviewed"
                  }
                  aria-label={
                    inContinueMode
                      ? "Done — exit continue lane"
                      : isThisEditing
                        ? "Done — exit edit mode"
                        : "Done — mark lane as reviewed"
                  }
                >
                  <i className="fas fa-check" aria-hidden />
                </button>
              </div>
              <div className="paths-list-inline-form-header">
                <div className="paths-list-inline-form-header-copy">
                  <p className="paths-list-inline-form-title">
                    {inContinueMode
                      ? "Continue Placing Points"
                      : isThisEditing
                        ? "Edit Waypoints"
                        : "Review Geometry"}
                  </p>
                  <p className="paths-list-inline-form-subtitle">
                    {inContinueMode
                      ? "Click on the map to append new points to the end of this lane. Click Done when finished."
                      : isThisEditing
                        ? "Drag waypoints to reposition, Ctrl+drag to lasso-select, right-click or press Delete to remove. Use Continue lane to append new points."
                        : "Adjust waypoints if something looks off, or confirm and move on."}
                  </p>
                </div>
                <span className="paths-list-inline-form-badge">
                  {inContinueMode
                    ? `${lane.waypoints?.length ?? 0} PT${(lane.waypoints?.length ?? 0) !== 1 ? "S" : ""}`
                    : isThisEditing
                      ? "Editing"
                      : "Optional"}
                </span>
              </div>
              {!inContinueMode && (
                <div className="paths-list-inline-form-actions">
                  {isThisEditing ? (
                    <>
                      <button
                        type="button"
                        className="paths-list-inline-form-create"
                        onClick={() => {
                          onExitEditPoints?.();
                          onResumeManualLane?.(lane.id);
                        }}
                        title="Continue placing points"
                      >
                        <i className="fas fa-pen" aria-hidden /> Continue lane
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="paths-list-inline-form-create"
                      onClick={() => onEditPoints?.(lane.id)}
                      disabled={!hasLaneGeometry}
                    >
                      <i className="fa-solid fa-pen-to-square" aria-hidden />{" "}
                      Edit Lane
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {showSpeedSummary && (
        <div
          className={`paths-list-speed-summary${isThisSpeedEditing ? " paths-list-speed-summary--editing" : ""}${attachSpeedSummaryToChecklist ? " paths-list-speed-summary--attached" : ""}`}
        >
          {isThisSpeedEditing && (
            <>
              <p className="paths-list-step-label paths-list-step-label--outer">
                <button
                  type="button"
                  className="paths-list-back-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExitSpeedMode?.();
                    // Manual lanes: drop back into placement so the user can
                    // refine points instead of losing the geometry they just
                    // committed via Done. Offset/copy lanes are cheap to
                    // regenerate from the form, so keep the discard behavior.
                    if (canResumeManual) {
                      onResumeManualLane?.(lane.id);
                    } else {
                      onDelete?.(lane.id);
                    }
                  }}
                  title={
                    canResumeManual
                      ? "Go back — refine points"
                      : "Go back — discard this lane"
                  }
                  aria-label={
                    canResumeManual
                      ? "Go back — refine points"
                      : "Go back — discard this lane"
                  }
                >
                  <i className="fa-solid fa-arrow-left" aria-hidden />
                </button>
                <span className="paths-list-step-badge paths-list-step-badge--current">
                  2
                </span>
                Step 2 of 3 · Speeds
              </p>
              <div className="paths-list-speed-summary-meta">
                <div className="paths-list-speed-summary-meta-start">
                  <span className="paths-list-speed-summary-badge">
                    Speed Selector
                  </span>
                  <button
                    type="button"
                    className="paths-list-inline-form-done"
                    onClick={(e) => {
                      e.stopPropagation();
                      const hasZero = speedSegments.some(
                        (s) => !Number(s?.speedMph),
                      );
                      if (hasZero) {
                        setShowZeroSpeedError(true);
                        return;
                      }
                      setShowZeroSpeedError(false);
                      onExitSpeedMode?.();
                    }}
                    title="Done"
                    aria-label="Done — exit speed mode"
                  >
                    <i className="fas fa-check" aria-hidden />
                  </button>
                </div>
                <span className="paths-list-speed-summary-total">
                  Total:{" "}
                  {totalMiles > 0 ? `${fmtMile(totalMiles)} mi` : "\u2014"}
                </span>
              </div>
              <p className="paths-list-speed-summary-hint">
                Click the lane on the map to add breakpoints, then edit each
                segment speed directly in the table.
              </p>
            </>
          )}
          {speedSegments.length > 0 ? (
            <table className="paths-list-speed-table">
              <thead>
                <tr>
                  <th>
                    <div className="paths-list-speed-table-head-mile">
                      <span>Mileage</span>
                      {isThisSpeedEditing && (
                        <button
                          type="button"
                          className="paths-list-speed-table-add"
                          onClick={(e) => {
                            e.stopPropagation();
                            const lastMile =
                              speedSegments[speedSegments.length - 1]
                                ?.mileMark ?? 0;
                            const newMile = (lastMile + totalMiles) / 2;
                            if (newMile > lastMile && newMile < totalMiles) {
                              onAddSpeedBreakpoint?.(lane.id, newMile, 0);
                            }
                          }}
                          title="Add speed segment"
                          aria-label="Add speed segment"
                        >
                          <i className="fa-solid fa-plus" aria-hidden />
                        </button>
                      )}
                    </div>
                  </th>
                  <th>Speed</th>
                </tr>
              </thead>
              <tbody>
                {speedSegments.map((seg, idx) => {
                  const startMile = Number(seg.mileMark);
                  const nextSeg = speedSegments[idx + 1];
                  const endMile = nextSeg
                    ? Number(nextSeg.mileMark)
                    : totalMiles;
                  const speedMph = Number.isFinite(Number(seg?.speedMph))
                    ? Number(seg.speedMph)
                    : 0;

                  return (
                    <tr key={`${lane.id}-speed-${idx}`}>
                      <td className="paths-list-speed-table-mileage">
                        {isThisSpeedEditing && editingMileRowIdx === idx ? (
                          <div className="paths-list-speed-table-mileage-editor">
                            <input
                              type="number"
                              className="paths-list-speed-table-mileage-input"
                              value={startMile.toFixed(3)}
                              step={0.01}
                              min={speedSegments[idx - 1]?.mileMark ?? 0}
                              max={endMile}
                              disabled={idx === 0}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                if (Number.isFinite(v))
                                  onUpdateSpeedBreakpointMile?.(
                                    lane.id,
                                    idx,
                                    v,
                                  );
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              title={
                                idx === 0
                                  ? "Start of lane — fixed at 0"
                                  : "Start mile"
                              }
                            />
                            <span className="paths-list-speed-table-mileage-sep">
                              –
                            </span>
                            <input
                              type="number"
                              className="paths-list-speed-table-mileage-input"
                              value={endMile.toFixed(3)}
                              step={0.01}
                              min={startMile}
                              max={
                                speedSegments[idx + 2]
                                  ? speedSegments[idx + 2].mileMark
                                  : totalMiles
                              }
                              disabled={!nextSeg}
                              onChange={(e) => {
                                const v = Number(e.target.value);
                                if (Number.isFinite(v) && nextSeg)
                                  onUpdateSpeedBreakpointMile?.(
                                    lane.id,
                                    idx + 1,
                                    v,
                                  );
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              title={
                                !nextSeg ? "End of lane — fixed" : "End mile"
                              }
                            />
                            <button
                              type="button"
                              className="paths-list-speed-table-mileage-done"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingMileRowIdx(null);
                              }}
                              title="Done editing mile"
                              aria-label="Done editing mile"
                            >
                              <i className="fas fa-check" aria-hidden />
                            </button>
                          </div>
                        ) : (
                          <div className="paths-list-speed-table-mileage-display">
                            <span>
                              {fmtMile(startMile)} - {fmtMile(endMile)} mi
                            </span>
                            {isThisSpeedEditing && (
                              <button
                                type="button"
                                className="paths-list-speed-table-mileage-edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingMileRowIdx(idx);
                                }}
                                title="Edit mileage"
                                aria-label="Edit mileage"
                              >
                                <i className="fa-solid fa-pen" aria-hidden />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="paths-list-speed-table-speed">
                        {isThisSpeedEditing ? (
                          <div className="paths-list-speed-table-speed-editor">
                            <div className="paths-list-speed-table-speed-input-shell">
                              <input
                                type="number"
                                ref={idx === 0 ? firstSpeedInputRef : null}
                                className={`paths-list-speed-table-speed-input${showZeroSpeedError && !speedMph ? " paths-list-speed-table-speed-input--invalid" : ""}`}
                                // Show an empty field (not a literal "0") when the
                                // speed is unset, so the user isn't typing after a
                                // pre-filled 0 → "035". (React's number-input
                                // reconciliation uses loose equality, so once "035"
                                // is in the DOM it never gets rewritten to "35"
                                // because "035" == 35.) select() on focus lets an
                                // existing value be replaced cleanly too.
                                value={speedMph || ""}
                                min={0}
                                max={200}
                                onChange={(e) =>
                                  onUpdateSpeedAtBreakpoint?.(
                                    lane.id,
                                    idx,
                                    Number(e.target.value),
                                  )
                                }
                                onFocus={(e) => e.target.select()}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              />
                              <span className="paths-list-speed-table-speed-unit">
                                mph
                              </span>
                            </div>
                            {idx > 0 ? (
                              <button
                                type="button"
                                className="paths-list-speed-table-delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveSpeedBreakpoint?.(lane.id, idx);
                                }}
                                title="Delete this speed segment"
                                aria-label="Delete speed segment"
                              >
                                <i
                                  className="fa-solid fa-trash-can"
                                  aria-hidden
                                />
                              </button>
                            ) : (
                              <span
                                className="paths-list-speed-table-delete-placeholder"
                                aria-hidden
                              />
                            )}
                          </div>
                        ) : (
                          `${speedMph} mph`
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="paths-list-speed-summary-empty">
              No speed segments yet. Click directly on the lane to add the first
              breakpoint.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Path Row ─────────────────────────────────────────────────────────────────

function PathRow({
  flowScope = "routes",
  editingRouteId = null,
  onEditRoute,
  onCancelEditRoute,
  path,
  lanes,
  allPaths,
  isActive,
  activeLaneId,
  laneEditMode,
  editingLaneId,
  speedEditMode,
  segmentColors,
  wktAvailable,
  manualLaneCreationActive,
  manualLanePointCount,
  selectedLaneWaypointIndices,
  onClearLaneSelection,
  onSelect,
  onDelete,
  onToggleVisible,
  onRename,
  onGoTo,
  onSelectLane,
  onDeleteLane,
  onToggleLaneVisible,
  onRenameLane,
  onChangeLaneColor,
  onChangeLanePosition,
  onChangeLaneDirection,
  onEditLanePoints,
  onExitEditLanePoints,
  onEnterSpeedMode,
  onExitSpeedMode,
  onUpdateSpeedAtBreakpoint,
  onUpdateSpeedBreakpointMile,
  onAddSpeedBreakpoint,
  onRemoveSpeedBreakpoint,
  onAddLane,
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  onResumeManualLane,
  onMarkReviewed,
  hasMaps2000Folder,
  onCreateOrthoMaps,
  isCreatingOrthoMaps,
  orthoProgress,
  onStartAutoTrace,
  onContinueAutoTrace,
  laneTraceView,
  laneTraceStopHere = false,
  laneTraceProgress = {},
  onEnterBridgeManually,
  onRefreshOrthoMaps,
  loadingOrthoTiles = false,
  hasFoldersSelected = false,
  isDeleting = false,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // null | 'picker' | 'offset' | 'copy' | 'manual'
  const [addingLane, setAddingLane] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [orthoBtnHeartbeat, setOrthoBtnHeartbeat] = useState(false);

  const color = segmentColors[(path.colorIndex ?? 0) % segmentColors.length];
  const lenLabel = fmtLength(path.pathData, path.waypoints);
  const lrsWarning = getLrsLengthWarning(path);
  const pathLanes = lanes.filter((l) => l.pathId === path.id);
  const hasUnreviewedLane = pathLanes.some((lane) => lane.reviewed === false);
  const hasPath = Boolean(path.pathData?.geometry?.coordinates?.length);
  const waypointCount = path.waypoints?.length ?? 0;
  const orthoBtnEligible = waypointCount >= 2;
  // A route must be named before its ortho maps can be created. Blank/whitespace
  // labels (the "Road Name" placeholder state) don't count.
  const hasValidName = String(path.label ?? "").trim().length > 0;
  const orthoPhase = selectOrthoPhase({
    orthoJob: path.orthoJob,
    hasMaps2000Folder,
    isLiveCreating: isCreatingOrthoMaps,
  });
  const orthoView = selectOrthoProgress({
    orthoJob: path.orthoJob,
    liveProgress: orthoProgress,
  });
  const orthoRunning = orthoPhase === "running";
  const isEditingThisRoute = editingRouteId === path.id;
  const isLockedForOrtho = orthoPhase === "done" && !isEditingThisRoute;
  const [showEditWarn, setShowEditWarn] = useState(false);
  // Route-actions block visibility. Routes scope always shows it (Create Ortho
  // or the "go to Lanes" hint) — lane edit/speed state is a Lanes-tab concern
  // and must not blank the Routes action. Lanes scope hides it while the lane
  // picker is open or a lane still needs review.
  const showRouteActions =
    flowScope === "routes"
      ? true
      : !laneEditMode &&
        !speedEditMode &&
        addingLane === null &&
        !hasUnreviewedLane;

  // Collapse the picker when this path becomes inactive
  useEffect(() => {
    if (!isActive) setAddingLane(null);
  }, [isActive]);

  // One-shot heartbeat on the Create Ortho button when waypoint count crosses
  // from <2 to >=2, signalling the user that this is the next available action.
  const prevOrthoBtnEligibleRef = useRef(orthoBtnEligible);
  useEffect(() => {
    if (orthoBtnEligible && !prevOrthoBtnEligibleRef.current) {
      setOrthoBtnHeartbeat(true);
      const t = setTimeout(() => setOrthoBtnHeartbeat(false), 2400);
      prevOrthoBtnEligibleRef.current = true;
      return () => clearTimeout(t);
    }
    prevOrthoBtnEligibleRef.current = orthoBtnEligible;
  }, [orthoBtnEligible]);

  function handleAddLaneClick() {
    onAddLane?.(path.id);
    setAddingLane("picker");
  }

  async function handleCreateOffset(args) {
    await onCreateOffsetLane?.(args);
    setAddingLane(null);
  }

  return (
    <div
      className={`paths-list-path-block${isActive ? " paths-list-path-block--active" : ""}${isDeleting ? " paths-list-path-block--deleting" : ""}`}
    >
      {isDeleting && (
        <div
          className="paths-list-path-deleting-overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <i className="fa-solid fa-spinner fa-spin" aria-hidden />
          <span className="paths-list-path-deleting-overlay-text">
            Deleting…
          </span>
        </div>
      )}
      <div className="paths-list-path-expanded">
        <div
          className="paths-list-path-header"
          onClick={() => onSelect(path.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            // Don't intercept keystrokes meant for the InlineLabel input.
            if (e.target?.tagName === "INPUT") return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(path.id);
            }
          }}
          aria-label={`Select route ${path.label}`}
          style={{ cursor: "pointer" }}
        >
          <div className="paths-list-path-info">
            <InlineLabel
              value={path.label}
              onChange={(v) => onRename(path.id, v)}
              placeholder="Road Name"
              leadingIcon={
                <i
                  className="fa-solid fa-location-dot paths-list-dot"
                  style={{ color }}
                  aria-hidden
                />
              }
            />
            <span className="paths-list-meta">
              {path.waypoints?.length ?? 0} points
              {lenLabel ? ` · ${lenLabel}` : ""}
              {pathLanes.length > 0
                ? ` · ${pathLanes.length} lane${pathLanes.length !== 1 ? "s" : ""}`
                : ""}
            </span>
            {lrsWarning && (
              <span
                className="paths-list-lrs-warning"
                title={lrsWarning}
                role="img"
                aria-label="Route length differs from milepost span"
              >
                <i className="fas fa-triangle-exclamation" aria-hidden /> length differs from MP span
              </span>
            )}
          </div>
          <div
            className="paths-list-path-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {hasMaps2000Folder &&
              onRefreshOrthoMaps &&
              (() => {
                const refreshDisabled =
                  loadingOrthoTiles || !hasFoldersSelected;
                return (
                  <button
                    type="button"
                    className={`paths-panel-waypoints-goto-btn${refreshDisabled ? " paths-panel-waypoints-goto-btn-disabled" : ""}`}
                    onClick={refreshDisabled ? undefined : onRefreshOrthoMaps}
                    disabled={refreshDisabled}
                    title={
                      loadingOrthoTiles
                        ? "Loading ortho map tiles…"
                        : !hasFoldersSelected
                          ? "Select a LAS folder first to reload ortho maps"
                          : "Reload ortho map tiles from Drive"
                    }
                    aria-label="Refresh ortho maps"
                  >
                    <i
                      className={`fas fa-sync-alt${loadingOrthoTiles ? " fa-spin" : ""}`}
                      aria-hidden
                    />
                  </button>
                );
              })()}
            <button
              type="button"
              className={`paths-panel-waypoints-goto-btn${!onGoTo || !hasPath ? " paths-panel-waypoints-goto-btn-disabled" : ""}`}
              onClick={() => {
                if (onGoTo && hasPath) onGoTo(path.id);
              }}
              title={
                hasPath
                  ? "Center map on this path"
                  : "Route this path to enable go to"
              }
              aria-label="Go to path"
              disabled={!onGoTo || !hasPath}
            >
              <i className="fa-solid fa-location-crosshairs" aria-hidden />
            </button>
            <button
              type="button"
              className="paths-panel-waypoints-eye-btn"
              onClick={() => onToggleVisible(path.id)}
              title={path.visible ? "Hide path" : "Show path"}
              aria-label={path.visible ? "Hide path" : "Show path"}
            >
              <i
                className={`fas ${path.visible ? "fa-eye" : "fa-eye-slash"}`}
                aria-hidden
              />
            </button>
            <button
              type="button"
              className="paths-panel-waypoints-delete-segment-btn"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete path and its lanes"
              aria-label="Delete path"
            >
              <i className="fa-solid fa-trash-can" aria-hidden />
            </button>
          </div>
        </div>

        {showDeleteConfirm && (
          <div
            className="delete-modal-overlay"
            {...backdropCloseHandlers(() => {
              setShowDeleteConfirm(false);
              setDeleteConfirmText("");
            })}
          >
            <div
              className="delete-modal delete-modal--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-modal-header">
                <i className="fas fa-exclamation-triangle"></i>
                <h2>Delete Route?</h2>
              </div>
              <div className="delete-modal-body">
                <p>
                  Delete <strong>"{path.label}"</strong> with all it's lanes and
                  orthomaps? This action cannot be undone.
                </p>
                <p className="rsa-route-delete-instruction">
                  To confirm deletion, type{" "}
                  <strong>
                    <em>permanently delete</em>
                  </strong>{" "}
                  in the text input field.
                </p>
                <input
                  type="text"
                  className="rsa-route-delete-input"
                  placeholder="permanently delete"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  onKeyDown={(e) => {
                    // Stop shortcuts (e.g. Backspace/Delete waypoint delete) from swallowing input keys.
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                  autoFocus
                />
              </div>
              <div className="delete-modal-footer">
                <button
                  className="delete-modal-btn delete-modal-btn--cancel"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                  }}
                >
                  Cancel
                </button>
                <button
                  className="delete-modal-btn delete-modal-btn--confirm"
                  disabled={deleteConfirmText !== "permanently delete"}
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText("");
                    onDelete(path.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showEditWarn && (
          <div
            className="delete-modal-overlay"
            {...backdropCloseHandlers(() => setShowEditWarn(false))}
          >
            <div
              className="delete-modal delete-modal--wide"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-modal-header">
                <i className="fas fa-exclamation-triangle"></i>
                <h2>Edit this route?</h2>
              </div>
              <div className="delete-modal-body">
                <p>
                  This route is linked to the ortho maps generated from it.
                  Editing the route will require regenerating its maps before
                  you can continue, and any lanes you've drawn from this route
                  may need review.
                </p>
              </div>
              <div className="delete-modal-footer">
                <button
                  className="delete-modal-btn delete-modal-btn--cancel"
                  onClick={() => setShowEditWarn(false)}
                >
                  Cancel
                </button>
                <button
                  className="delete-modal-btn delete-modal-btn--confirm"
                  onClick={() => {
                    setShowEditWarn(false);
                    onEditRoute?.(path.id);
                  }}
                >
                  Edit Route
                </button>
              </div>
            </div>
          </div>
        )}

        {flowScope === "lanes" &&
          pathLanes.map((lane) => (
            <LaneRow
              key={lane.id}
              lane={lane}
            isActive={lane.id === activeLaneId}
            laneEditMode={laneEditMode}
            editingLaneId={editingLaneId}
            speedEditMode={speedEditMode}
            segmentColors={segmentColors}
            manualLaneCreationActive={manualLaneCreationActive}
            selectedLaneWaypointIndices={selectedLaneWaypointIndices}
            onClearLaneSelection={onClearLaneSelection}
            onSelect={onSelectLane}
            onDelete={onDeleteLane}
            onToggleVisible={onToggleLaneVisible}
            onRename={onRenameLane}
            onChangeLaneColor={onChangeLaneColor}
            onChangeLanePosition={onChangeLanePosition}
            onChangeLaneDirection={onChangeLaneDirection}
            onEditPoints={onEditLanePoints}
            onExitEditPoints={onExitEditLanePoints}
            onEnterSpeedMode={onEnterSpeedMode}
            onExitSpeedMode={onExitSpeedMode}
            onUpdateSpeedAtBreakpoint={onUpdateSpeedAtBreakpoint}
            onUpdateSpeedBreakpointMile={onUpdateSpeedBreakpointMile}
            onAddSpeedBreakpoint={onAddSpeedBreakpoint}
            onRemoveSpeedBreakpoint={onRemoveSpeedBreakpoint}
            onResumeManualLane={onResumeManualLane}
            onStopManualLane={onStopManualLane}
            onMarkReviewed={onMarkReviewed}
            onEnterBridgeManually={onEnterBridgeManually}
            laneTraceProgress={laneTraceProgress}
          />
        ))}

        {flowScope === "lanes" && !laneEditMode && !speedEditMode && (
          <>
            {addingLane !== null && !manualLaneCreationActive && (
              <div
                className="paths-list-lane-row paths-list-lane-row--active paths-list-lane-row--preview"
                aria-hidden="true"
              >
                <div className="paths-list-lane-top">
                  <div className="paths-list-lane-info">
                    <div className="paths-list-lane-edit-row">
                      <span className="paths-list-label-text">
                        <i
                          className="fa-solid fa-location-dot paths-list-dot"
                          style={{ color }}
                          aria-hidden
                        />
                        <span className="paths-list-label-value">New Lane</span>
                      </span>
                    </div>
                    <span className="paths-list-meta">—</span>
                  </div>
                </div>
              </div>
            )}

            {addingLane === "picker" && (
              <div
                className="paths-list-lane-picker"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="paths-list-step-label paths-list-step-label--outer">
                  <span className="paths-list-step-badge paths-list-step-badge--current">
                    1
                  </span>
                  Step 1 of 3 · Add Lane
                </p>
                <div className="paths-list-lane-picker-header">
                  <div>
                    <p className="paths-list-lane-picker-title">
                      Choose precise lane creation mode
                    </p>
                    <p className="paths-list-lane-picker-subtitle">
                      Create a computed offset lane or draw one point-by-point
                      on the map.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="paths-list-lane-picker-cancel"
                    onClick={() => setAddingLane(null)}
                    aria-label="Cancel lane picker"
                    title="Close lane creation options"
                  >
                    <i className="fas fa-times" aria-hidden />
                  </button>
                </div>
                <div className="paths-list-lane-picker-options">
                  <button
                    type="button"
                    className="paths-list-lane-picker-btn paths-list-lane-picker-btn--offset"
                    onClick={() => setAddingLane("offset")}
                  >
                    <div className="paths-list-lane-picker-btn-content">
                      <span className="paths-list-lane-picker-btn-copy">
                        <span className="paths-list-lane-picker-btn-header">
                          <span className="paths-list-lane-picker-btn-header-main">
                            <span
                              className="paths-list-lane-picker-btn-icon"
                              aria-hidden
                            >
                              <i
                                className="fas fa-arrow-right-arrow-left"
                                aria-hidden
                              />
                            </span>
                            <span className="paths-list-lane-picker-btn-title">
                              Offset
                            </span>
                          </span>
                        </span>
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="paths-list-lane-picker-btn paths-list-lane-picker-btn--copy"
                    onClick={() => setAddingLane("copy")}
                  >
                    <div className="paths-list-lane-picker-btn-content">
                      <span className="paths-list-lane-picker-btn-copy">
                        <span className="paths-list-lane-picker-btn-header">
                          <span className="paths-list-lane-picker-btn-header-main">
                            <span
                              className="paths-list-lane-picker-btn-icon"
                              aria-hidden
                            >
                              <i className="fas fa-clone" aria-hidden />
                            </span>
                            <span className="paths-list-lane-picker-btn-title">
                              Copy
                            </span>
                          </span>
                        </span>
                      </span>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="paths-list-lane-picker-btn paths-list-lane-picker-btn--manual"
                    onClick={() => {
                      onStartManualLane?.(path.id);
                      setAddingLane("manual");
                    }}
                  >
                    <div className="paths-list-lane-picker-btn-content">
                      <span className="paths-list-lane-picker-btn-copy">
                        <span className="paths-list-lane-picker-btn-header">
                          <span className="paths-list-lane-picker-btn-header-main">
                            <span
                              className="paths-list-lane-picker-btn-icon"
                              aria-hidden
                            >
                              <i className="fas fa-pen" aria-hidden />
                            </span>
                            <span className="paths-list-lane-picker-btn-title">
                              Manual
                            </span>
                          </span>
                        </span>
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {addingLane === "offset" && (
              <InlineOffsetForm
                paths={allPaths}
                lanes={lanes}
                activePathId={path.id}
                wktAvailable={wktAvailable}
                onCreateOffsetLane={handleCreateOffset}
                onCancel={() => setAddingLane("picker")}
              />
            )}

            {addingLane === "copy" && (
              <InlineOffsetForm
                paths={allPaths}
                lanes={lanes}
                activePathId={path.id}
                wktAvailable={wktAvailable}
                onCreateOffsetLane={handleCreateOffset}
                onCancel={() => setAddingLane("picker")}
                mode="copy"
              />
            )}

            {addingLane === "manual" && (
              <InlineManualControls
                pathId={path.id}
                manualLaneCreationActive={manualLaneCreationActive}
                manualLanePointCount={manualLanePointCount}
                onStartManualLane={onStartManualLane}
                onStopManualLane={onStopManualLane}
                onCancel={() => setAddingLane(null)}
                onBackToPicker={() => {
                  // The Manual picker button auto-creates a placeholder lane.
                  // Discard it on back-out only if the user hasn't placed any
                  // points yet — otherwise we would silently destroy their
                  // work and replace it with a gray "New Lane" preview.
                  if (activeLaneId) {
                    const lane = lanes.find((l) => l.id === activeLaneId);
                    const noPointsPlaced =
                      !Array.isArray(lane?.waypoints) ||
                      lane.waypoints.length === 0;
                    if (noPointsPlaced) onDeleteLane?.(activeLaneId);
                  }
                  setAddingLane("picker");
                }}
              />
            )}
          </>
        )}

        {showRouteActions && (
            <div className="paths-list-path-add-lane">
              <span className="paths-list-path-add-lane-label">
                Route actions
              </span>
              {orthoPhase === "done" ? (
                flowScope === "lanes" ? (
                <>
                  <button
                    type="button"
                    className="paths-list-add-lane-btn"
                    onClick={handleAddLaneClick}
                    disabled={!isActive}
                  >
                    <i className="fas fa-plus" aria-hidden /> Add Lane
                  </button>
                  {laneTraceView ? (
                    <div className="paths-list-create-ortho-loading-content">
                      <div className="paths-list-create-ortho-loading-label">
                        <span className="paths-list-create-ortho-spinner" />
                        Tracing lane…
                      </div>
                      <div className="paths-list-create-ortho-progress-track">
                        <div
                          className="paths-list-create-ortho-progress-fill"
                          style={{ width: `${laneTraceView?.progress || 0}%` }}
                        />
                      </div>
                      {laneTraceView?.message && (
                        <span className="paths-list-create-ortho-progress-label">
                          {laneTraceView.message}
                        </span>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="paths-list-add-lane-btn"
                      onClick={() => onStartAutoTrace?.(path.id)}
                      disabled={!isActive}
                      title="Auto-trace a lane: click a start point on the marking, then a second point in the direction to trace"
                    >
                      <i className="fas fa-wand-magic-sparkles" aria-hidden />{" "}
                      Auto-trace Lane
                    </button>
                  )}
                  {laneTraceStopHere && !laneTraceView && (
                    <button
                      type="button"
                      className="paths-list-add-lane-btn"
                      onClick={() => onContinueAutoTrace?.()}
                      disabled={!isActive}
                      title="Trace stopped at a gap/intersection — seed past it to continue this lane"
                    >
                      <i className="fas fa-forward-step" aria-hidden /> Continue
                      trace
                    </button>
                  )}
                </>
                ) : isRouteGeometryDirty(path) ? (
                  <button
                    type="button"
                    className={`paths-list-add-lane-btn paths-list-create-ortho-btn${orthoRunning ? " paths-list-create-ortho-btn--loading" : ""}`}
                    onClick={() =>
                      onCreateOrthoMaps?.(path.id, { mode: "edit" })
                    }
                    disabled={!isActive || orthoRunning || !hasValidName}
                    title={
                      !hasValidName
                        ? "Enter a road name for this route first"
                        : undefined
                    }
                  >
                    {orthoRunning ? (
                      <span className="paths-list-create-ortho-loading-content">
                        <span className="paths-list-create-ortho-spinner" />{" "}
                        Updating Ortho Maps…
                      </span>
                    ) : (
                      <>
                        <i className="fa-regular fa-map" aria-hidden />{" "}
                        Create Orthomaps for edit
                      </>
                    )}
                  </button>
                ) : isEditingThisRoute ? (
                  <button
                    type="button"
                    className="paths-list-add-lane-btn"
                    onClick={() => onCancelEditRoute?.()}
                    disabled={!isActive}
                  >
                    <i className="fas fa-check" aria-hidden /> Done editing
                  </button>
                ) : (
                  <>
                    <div className="paths-list-path-scope-hint paths-list-path-scope-hint--done">
                      <i className="fas fa-circle-check" aria-hidden />
                      <span>
                        Ortho maps ready — open the <strong>Lanes</strong> tab
                        to add lanes.
                      </span>
                    </div>
                    <button
                      type="button"
                      className="paths-list-add-lane-btn paths-list-edit-route-btn"
                      onClick={() => setShowEditWarn(true)}
                      disabled={!isActive}
                    >
                      <i className="fas fa-pen" aria-hidden /> Edit Route
                    </button>
                  </>
                )
              ) : flowScope === "routes" ? (
                <button
                  type="button"
                  className={`paths-list-add-lane-btn paths-list-create-ortho-btn${orthoRunning ? " paths-list-create-ortho-btn--loading" : ""}${orthoBtnHeartbeat ? " paths-list-create-ortho-btn--heartbeat" : ""}`}
                  onClick={() => onCreateOrthoMaps?.(path.id)}
                  disabled={
                    !isActive ||
                    orthoRunning ||
                    !orthoBtnEligible ||
                    !hasValidName
                  }
                  title={
                    !orthoBtnEligible
                      ? "Add at least 2 path points to create ortho maps"
                      : !hasValidName
                        ? "Enter a road name for this route first"
                        : undefined
                  }
                >
                  {orthoRunning ? (
                    <div className="paths-list-create-ortho-loading-content">
                      <div className="paths-list-create-ortho-loading-label">
                        <span className="paths-list-create-ortho-spinner" />
                        Creating Ortho Maps…
                      </div>
                      <div className="paths-list-create-ortho-progress-track">
                        <div
                          className="paths-list-create-ortho-progress-fill"
                          style={{ width: `${orthoView?.progress || 0}%` }}
                        />
                      </div>
                      {orthoView?.message && (
                        <span className="paths-list-create-ortho-progress-label">
                          {orthoView.message}
                        </span>
                      )}
                    </div>
                  ) : (
                    <>
                      <i className="fa-regular fa-map" aria-hidden /> Create
                      Ortho Maps
                    </>
                  )}
                </button>
              ) : (
                <div className="paths-list-path-scope-hint">
                  <i className="fas fa-circle-info" aria-hidden />
                  <span>
                    Create ortho maps for this route on the{" "}
                    <strong>Routes</strong> tab first.
                  </span>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Lane Edit Toolbar ────────────────────────────────────────────────────────

function LaneEditToolbar({
  selectedCount,
  onClearSelection,
  onExit,
  onContinueLane,
}) {
  return (
    <div className="paths-list-edit-toolbar">
      <button
        type="button"
        className="paths-list-edit-toolbar-exit"
        onClick={onExit}
        title="Exit lane point edit mode"
      >
        <i className="fas fa-times" aria-hidden /> Exit Edit Mode
      </button>
      {onContinueLane && (
        <button
          type="button"
          className="paths-list-edit-toolbar-continue"
          onClick={onContinueLane}
          title="Continue placing points on this lane"
        >
          <i className="fas fa-pen" aria-hidden /> Continue lane
        </button>
      )}
      <div className="paths-list-edit-toolbar-meta">
        <span
          className="paths-list-edit-toolbar-count"
          title="Hold Ctrl and drag to lasso-select points"
        >
          {selectedCount} point{selectedCount !== 1 ? "s" : ""} selected
        </span>
        {selectedCount > 0 && (
          <button
            type="button"
            className="paths-list-edit-toolbar-clear"
            onClick={onClearSelection}
            title="Clear selection"
            aria-label="Clear selection"
          >
            <i className="fas fa-trash" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Speed Edit Toolbar ──────────────────────────────────────────────────────

function SpeedEditToolbar({ lane, onUpdateSpeed, onRemoveBreakpoint, onExit }) {
  void lane;
  void onUpdateSpeed;
  void onRemoveBreakpoint;
  void onExit;
  return null;
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * PathsListPanel — Hierarchical list of independent paths and their lanes.
 *
 * Props:
 *   paths, lanes                         — from useRsaPaths state
 *   activePathId, activeLaneId           — active selections
 *   laneEditMode                         — boolean: rubber-band editing active
 *   selectedLaneWaypointIndices          — number[]
 *   segmentColors                        — SEGMENT_COLORS array
 *   wktAvailable                         — boolean: enables offset lane creation
 *   manualLaneCreationActive             — boolean
 *   manualLanePointCount                 — number
 *   onSelectPath, onDeletePath, onTogglePathVisible, onGoToPath, onRenamePath
 *   onSelectLane, onDeleteLane, onToggleLaneVisible, onRenameLane
 *   onChangeLaneColor, onChangeLanePosition, onChangeLaneDirection
 *   onAddPath, onAddLane
 *   onEditLanePoints, onExitEditLanePoints
 *   onClearLaneSelection
 *   onCreateOffsetLane                   — ({sourcePathId, directionMode, side, offsetFeet}) => Promise<void>
 *   onStartManualLane                    — (pathId: string) => void
 *   onStopManualLane                     — () => void
 */
export default function PathsListPanel({
  flowScope = "routes", // "routes" = build routes + ortho | "lanes" = add/edit lanes
  editingRouteId = null,
  onEditRoute,
  onCancelEditRoute,
  paths = [],
  lanes = [],
  activePathId,
  activeLaneId,
  laneEditMode = false,
  speedEditMode = false,
  selectedLaneWaypointIndices = [],
  segmentColors = SEGMENT_COLORS,
  wktAvailable = false,
  manualLaneCreationActive = false,
  manualLanePointCount = 0,
  onSelectPath,
  onDeletePath,
  onTogglePathVisible,
  onGoToPath,
  onRenamePath,
  onSelectLane,
  onDeleteLane,
  onToggleLaneVisible,
  onRenameLane,
  onChangeLaneColor,
  onChangeLanePosition,
  onChangeLaneDirection,
  onAddPath,
  onAddLane,
  onEditLanePoints,
  onExitEditLanePoints,
  onClearLaneSelection,
  onEnterSpeedMode,
  onExitSpeedMode,
  onUpdateSpeedAtBreakpoint,
  onUpdateSpeedBreakpointMile,
  onAddSpeedBreakpoint,
  onRemoveSpeedBreakpoint,
  onCreateOffsetLane,
  onStartManualLane,
  onStopManualLane,
  onResumeManualLane,
  onMarkReviewed,
  maps2000ByPath = {},
  onCreateOrthoMaps,
  creatingOrthoMapsByPath = {},
  orthoProgressByPath = {},
  onStartAutoTrace,
  onContinueAutoTrace,
  laneTraceProgress = {},
  laneTraceStop = null,
  bridge = null,
  onResumeAuto,
  onEndBridge,
  onEnterBridgeManually,
  onRefreshOrthoMaps,
  loadingOrthoTiles = false,
  hasFoldersSelected = false,
  deletingPathIds,
}) {
  const bridgedLane = bridge?.active
    ? lanes.find((l) => l.id === bridge.laneId)
    : null;
  const resumeAutoDisabled =
    !bridgedLane ||
    !Array.isArray(bridgedLane.waypoints) ||
    bridgedLane.waypoints.length < 2 ||
    Boolean(bridge?.pathId && laneTraceProgress?.[bridge.pathId]);

  // Lock "Add Route" while any existing route is still incomplete (ortho maps
  // not yet built) or dirty (edited since its maps were built). This forces the
  // user to finish one route end-to-end before starting the next one.
  const addRouteLocked =
    flowScope === "routes" &&
    paths.some((p) => {
      if (isRouteGeometryDirty(p)) return true;
      const phase = selectOrthoPhase({
        orthoJob: p.orthoJob,
        hasMaps2000Folder: maps2000ByPath[p.id] ?? null,
        isLiveCreating: creatingOrthoMapsByPath[p.id] ?? false,
      });
      return phase !== "done";
    });

  return (
    <div className="paths-list-panel">
      {bridge?.active && (
        <div
          className="paths-list-bridge-banner"
          role="status"
          aria-live="polite"
        >
          <div className="paths-list-bridge-banner__icon" aria-hidden>
            {bridge.entryMode === "manual" ? (
              <i className="fas fa-pen-to-square" />
            ) : (
              <i className="fas fa-triangle-exclamation" />
            )}
          </div>
          <div className="paths-list-bridge-banner__text">
            {bridge.entryMode === "lost_line_post_ai" ? (
              <>
                <strong>
                  Trace stopped — AI also couldn't find continuation.
                </strong>
                <br />
                Click the map to add bridge points. Right-click waypoints (or
                ctrl+drag to lasso, then Delete) to remove them.
              </>
            ) : bridge.entryMode === "lost_line" ? (
              <>
                <strong>Trace stopped at the orange marker.</strong>
                <br />
                Click the map to add bridge points. Right-click waypoints (or
                ctrl+drag to lasso, then Delete) to remove them.
              </>
            ) : (
              <>
                <strong>Editing lane.</strong>
                <br />
                Click the map to add points. Right-click waypoints (or ctrl+drag
                to lasso, then Delete) to remove them.
              </>
            )}
          </div>
          <div className="paths-list-bridge-banner__actions">
            <button
              type="button"
              className="paths-list-bridge-banner__btn paths-list-bridge-banner__btn--primary"
              onClick={onResumeAuto}
              disabled={resumeAutoDisabled}
              title={
                resumeAutoDisabled
                  ? "Place at least 2 anchors, and wait for any in-progress trace to finish"
                  : "Resume auto-trace from the last two waypoints"
              }
            >
              Resume Auto
            </button>
            <button
              type="button"
              className="paths-list-bridge-banner__btn"
              onClick={onEndBridge}
            >
              End Lane Here
            </button>
          </div>
        </div>
      )}
      {/* Add Route button — routes are created only in the routes flow */}
      {flowScope === "routes" && (
        <>
          <button
            type="button"
            className="paths-panel-add-segment-btn"
            onClick={onAddPath}
            disabled={addRouteLocked}
            title={
              addRouteLocked
                ? "Finish creating ortho maps for your current route before adding another"
                : undefined
            }
          >
            <i className="fas fa-plus" aria-hidden /> Add Route
          </button>
          {addRouteLocked && (
            <p className="paths-panel-add-segment-hint">
              <i className="fas fa-circle-info" aria-hidden /> Finish creating
              ortho maps for your current route before adding another.
            </p>
          )}
          <LrsRoutePanel disabled={addRouteLocked} />
        </>
      )}

      {/* Lanes tab has no routes to show lanes for yet — the map guidance
          tooltip only appears on the Routes tab, so this needs its own hint. */}
      {flowScope === "lanes" && paths.length === 0 && (
        <p className="paths-panel-add-segment-hint">
          <i className="fas fa-circle-info" aria-hidden /> Please create a
          route under the Routes tab.
        </p>
      )}

      {/* Path tree */}
      {paths.map((path) => (
        <PathRow
          key={path.id}
          flowScope={flowScope}
          editingRouteId={editingRouteId}
          onEditRoute={onEditRoute}
          onCancelEditRoute={onCancelEditRoute}
          path={path}
          lanes={lanes}
          allPaths={paths}
          isActive={path.id === activePathId}
          activeLaneId={activeLaneId}
          laneEditMode={laneEditMode}
          editingLaneId={activeLaneId}
          speedEditMode={speedEditMode}
          segmentColors={segmentColors}
          wktAvailable={wktAvailable}
          manualLaneCreationActive={manualLaneCreationActive}
          manualLanePointCount={manualLanePointCount}
          selectedLaneWaypointIndices={selectedLaneWaypointIndices}
          onClearLaneSelection={onClearLaneSelection}
          onSelect={onSelectPath}
          onDelete={onDeletePath}
          onToggleVisible={onTogglePathVisible}
          onRename={onRenamePath}
          onGoTo={onGoToPath}
          onSelectLane={onSelectLane}
          onDeleteLane={onDeleteLane}
          onToggleLaneVisible={onToggleLaneVisible}
          onRenameLane={onRenameLane}
          onChangeLaneColor={onChangeLaneColor}
          onChangeLanePosition={onChangeLanePosition}
          onChangeLaneDirection={onChangeLaneDirection}
          onEditLanePoints={onEditLanePoints}
          onExitEditLanePoints={onExitEditLanePoints}
          onEnterSpeedMode={onEnterSpeedMode}
          onExitSpeedMode={onExitSpeedMode}
          onUpdateSpeedAtBreakpoint={onUpdateSpeedAtBreakpoint}
          onUpdateSpeedBreakpointMile={onUpdateSpeedBreakpointMile}
          onAddSpeedBreakpoint={onAddSpeedBreakpoint}
          onRemoveSpeedBreakpoint={onRemoveSpeedBreakpoint}
          onAddLane={onAddLane}
          onCreateOffsetLane={onCreateOffsetLane}
          onStartManualLane={onStartManualLane}
          onStopManualLane={onStopManualLane}
          onResumeManualLane={onResumeManualLane}
          onMarkReviewed={onMarkReviewed}
          hasMaps2000Folder={maps2000ByPath[path.id] ?? null}
          onCreateOrthoMaps={onCreateOrthoMaps}
          isCreatingOrthoMaps={creatingOrthoMapsByPath[path.id] ?? false}
          orthoProgress={orthoProgressByPath[path.id] ?? null}
          onStartAutoTrace={onStartAutoTrace}
          onContinueAutoTrace={onContinueAutoTrace}
          laneTraceView={laneTraceProgress[path.id] ?? null}
          laneTraceStopHere={laneTraceStop?.pathId === path.id}
          laneTraceProgress={laneTraceProgress}
          onEnterBridgeManually={onEnterBridgeManually}
          onRefreshOrthoMaps={onRefreshOrthoMaps}
          loadingOrthoTiles={loadingOrthoTiles}
          hasFoldersSelected={hasFoldersSelected}
          isDeleting={deletingPathIds?.has(path.id) ?? false}
        />
      ))}
    </div>
  );
}
